import React, { useState, useEffect } from 'react'
import { ThreadDeals } from '@adthlete/shared-frontend'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MessageSquare, Send, Inbox, ArrowLeft, Handshake, Lock, Briefcase } from 'lucide-react'
import { PitchForm } from '@/components/messages/PitchForm'
import { cn } from '@/lib/utils'
import {
  useGetMessageThreadsQuery,
  useGetThreadMessagesQuery,
  useSendMessageReplyMutation,
  type DirectMessageThread,
} from '@/redux/messagesApi'
import { formatDistanceToNow } from 'date-fns'

const timeAgo = (date: string) => {
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true })
  } catch {
    return date
  }
}

// ─────────────────────────────────────────────
// Thread list row
// ─────────────────────────────────────────────
interface ThreadRowProps {
  name: string
  orgName?: string
  orgLogo?: string | null
  avatarSrc?: string | null
  subject?: string | null
  lastBody?: string
  isFromMe?: boolean
  unreadCount?: number
  lastMessageAt: string
  isActive: boolean
  onClick: () => void
}

const ThreadRow: React.FC<ThreadRowProps> = ({
  name,
  orgName,
  avatarSrc,
  subject,
  lastBody,
  isFromMe,
  unreadCount = 0,
  lastMessageAt,
  isActive,
  onClick,
}) => (
  <button
    onClick={onClick}
    className={cn(
      'w-full flex items-start gap-3 p-4 text-left transition-colors rounded-lg hover:bg-muted',
      isActive && 'bg-muted',
    )}
  >
    <div className="relative shrink-0">
      <Avatar className="h-10 w-10">
        <AvatarImage src={avatarSrc ?? undefined} alt={name} />
        <AvatarFallback className="bg-[#C0FD72]/40 text-foreground">
          {name.charAt(0)}
        </AvatarFallback>
      </Avatar>
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#C0FD72] border-2 border-border text-xs text-[#0F172A] flex items-center justify-center font-bold">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            'text-sm font-medium text-foreground truncate',
            unreadCount > 0 && 'font-semibold',
          )}
        >
          {name}
        </span>
        <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
          {timeAgo(lastMessageAt)}
        </span>
      </div>
      {(orgName || subject) && (
        <p className="text-xs text-muted-foreground truncate mt-0.5">
          {orgName}
          {orgName && subject ? ' • ' : ''}
          {subject}
        </p>
      )}
      {lastBody && (
        <p
          className={cn(
            'text-xs truncate mt-0.5',
            unreadCount > 0 ? 'text-foreground font-medium' : 'text-muted-foreground',
          )}
        >
          {isFromMe ? 'You: ' : ''}
          {lastBody}
        </p>
      )}
    </div>
  </button>
)

// ─────────────────────────────────────────────
// Thread conversation view
// ─────────────────────────────────────────────
interface ConversationProps {
  threadId: string
  onBack: () => void
}

const Conversation: React.FC<ConversationProps> = ({ threadId, onBack }) => {
  const [replyText, setReplyText] = useState('')
  const { data, isLoading } = useGetThreadMessagesQuery(threadId)
  const [pitching, setPitching] = useState(false)
  const [sendReply, { isLoading: isSending }] = useSendMessageReplyMutation()

  const handleSend = async () => {
    if (!replyText.trim() || isSending) return
    try {
      await sendReply({ threadId, message: replyText.trim() }).unwrap()
      setReplyText('')
    } catch (e) {
      console.error('Failed to send reply', e)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSend()
    }
  }

  const thread = data?.thread
  const senderName = thread?.contact?.name ?? 'Brand'
  const senderAvatar = thread?.contact?.avatar
  const orgName = thread?.org?.name

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border p-4 shrink-0">
        {/* Back button — visible on mobile, hidden on desktop */}
        <button
          onClick={onBack}
          className="md:hidden shrink-0 -ml-1 mr-1 p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Back to inbox"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Avatar className="h-9 w-9 shrink-0">
          <AvatarImage src={senderAvatar ?? undefined} alt={senderName} />
          <AvatarFallback className="bg-[#C0FD72]/40 text-foreground">
            {senderName.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm text-foreground truncate">{senderName}</p>
          {orgName && <p className="text-xs text-muted-foreground truncate">{orgName}</p>}
          {thread?.subject && (
            <p className="text-xs text-muted-foreground truncate">{thread.subject}</p>
          )}
        </div>
        {thread?.org ? (
          <button
            type="button"
            onClick={() => setPitching((p) => !p)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#C0FD72] px-3 py-2 text-sm font-semibold text-[#0F172A] hover:bg-[#B2F25E]"
          >
            <Handshake className="h-4 w-4" /> Pitch a deal
          </button>
        ) : null}
      </div>

      {pitching && thread ? (
        <PitchForm
          threadId={threadId}
          brandName={orgName ?? senderName}
          onDone={() => setPitching(false)}
        />
      ) : null}

      {thread?.contact?.role === 'manager' ? (
        <p className="flex items-center gap-1.5 border-b border-[#BFD7FB] bg-[#EFF6FF] px-4 py-2 text-xs font-semibold text-[#1D4ED8]">
          <Lock className="h-3.5 w-3.5 shrink-0" /> Private: just you and your manager. Brands
          never see this.
        </p>
      ) : (
        <ThreadDeals deals={thread?.deals ?? []} dealPath={(id) => `/deals/${id}`} />
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin h-5 w-5 border-2 border-[#C0FD72] border-t-transparent rounded-full" />
          </div>
        )}
        <div className="space-y-3">
          {data?.messages.map((msg) => (
            <div
              key={msg.id}
              className={cn('flex gap-2', msg.isFromMe ? 'flex-row-reverse' : 'flex-row')}
            >
              {!msg.isFromMe && (
                <Avatar className="h-7 w-7 shrink-0 mt-1">
                  <AvatarImage src={senderAvatar ?? undefined} alt={senderName} />
                  <AvatarFallback className="bg-[#C0FD72]/40 text-foreground text-xs">
                    {senderName.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              )}
              <div
                className={cn(
                  'rounded-2xl px-4 py-2.5 max-w-[75%] text-sm leading-relaxed',
                  msg.isFromMe
                    ? 'bg-[#C0FD72] text-[#0F172A] rounded-br-sm'
                    : 'bg-muted text-foreground rounded-bl-sm',
                )}
              >
                {msg.byline && (
                  <p className="text-xs font-medium opacity-70 mb-0.5">{msg.byline}</p>
                )}
                {msg.deal ? (
                  <Link
                    to={`/deals/${msg.deal.id}`}
                    className="mb-1 inline-flex max-w-full items-center gap-1 rounded-full bg-[#FFFFFF]/70 px-2 py-0.5 text-xs font-semibold text-[#1D4ED8] underline-offset-2 hover:underline"
                  >
                    <Briefcase className="h-3 w-3 shrink-0" />
                    <span className="truncate">
                      {[msg.deal.brand, msg.deal.title].filter(Boolean).join(' · ') || 'A deal'}
                    </span>
                  </Link>
                ) : null}
                <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                <p className={cn('text-xs mt-1 opacity-60', msg.isFromMe ? 'text-right' : '')}>
                  {timeAgo(msg.createdAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Reply box */}
      <div className="border-t border-border p-4 shrink-0">
        <div className="flex gap-2">
          <Textarea
            placeholder="Type a reply…"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            className="resize-none flex-1 bg-card border-border text-foreground placeholder:text-muted-foreground"
          />
          <Button
            onClick={handleSend}
            disabled={isSending || !replyText.trim()}
            size="icon"
            className="bg-[#C0FD72] hover:bg-[#B2F25E] text-[#0F172A] self-end h-9 w-9"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Main Messages Page
// ─────────────────────────────────────────────
const Messages: React.FC = () => {
  const { id: threadIdFromUrl } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(threadIdFromUrl || null)
  const [search, setSearch] = useState('')

  const { data, isLoading } = useGetMessageThreadsQuery()
  const threads = data?.threads ?? []

  const filteredThreads = threads.filter(
    (t) =>
      t.contact.name.toLowerCase().includes(search.toLowerCase()) ||
      (t.org?.name?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
      (t.subject?.toLowerCase().includes(search.toLowerCase()) ?? false),
  )

  // Sync URL changes to local state
  useEffect(() => {
    setSelectedThreadId(threadIdFromUrl || null)
  }, [threadIdFromUrl])

  const handleSelect = (thread: DirectMessageThread) => {
    navigate(`/messages/${thread.id}`)
  }

  const handleBack = () => {
    navigate('/messages')
  }

  // On mobile: show either the list OR the conversation (not both).
  // On desktop (md+): show side-by-side.
  const showList = !selectedThreadId
  const showConversation = !!selectedThreadId

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden -mx-4 -my-4 md:-mx-6 md:-my-6">
      {/* ── Thread list ─────────────────────────── */}
      {/* Mobile: full-screen when no thread selected. Hidden when conversation open. */}
      {/* Desktop: always visible as a fixed-width sidebar. */}
      <aside
        className={cn(
          'w-full md:w-80 md:min-w-[320px] md:max-w-[320px] border-r border-border flex flex-col shrink-0 overflow-x-hidden',
          showList ? 'flex' : 'hidden md:flex',
        )}
      >
        <div className="p-4 border-b border-border flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-[#4d7c0f]" />
          <h1 className="text-2xl font-bold text-foreground">Inbox</h1>
        </div>

        <div className="px-3 py-2">
          <Input
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm bg-card border-border text-foreground placeholder:text-muted-foreground"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {isLoading && (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-5 w-5 border-2 border-[#C0FD72] border-t-transparent rounded-full" />
            </div>
          )}
          {!isLoading && filteredThreads.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Inbox className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No messages yet</p>
              <p className="text-xs mt-1 opacity-60">Brands will message you here</p>
            </div>
          )}
          {filteredThreads.map((thread) => (
            <ThreadRow
              key={thread.id}
              name={thread.contact.name}
              orgName={
                thread.org?.name ??
                (thread.contact.role === 'manager' ? 'Your manager · private' : undefined)
              }
              avatarSrc={thread.contact.avatar}
              subject={thread.subject}
              lastBody={thread.lastMessage?.body}
              isFromMe={thread.lastMessage?.isFromMe}
              unreadCount={thread.unreadCount}
              lastMessageAt={thread.lastMessageAt}
              isActive={selectedThreadId === thread.id}
              onClick={() => handleSelect(thread)}
            />
          ))}
        </div>
      </aside>

      {/* ── Conversation panel ───────────────────── */}
      {/* Mobile: full-screen when a thread is selected. Hidden otherwise. */}
      {/* Desktop: always fills remaining space. */}
      <main
        className={cn(
          'flex-1 flex flex-col overflow-hidden min-w-0',
          showConversation ? 'flex' : 'hidden md:flex',
        )}
      >
        {selectedThreadId ? (
          <Conversation threadId={selectedThreadId} onBack={handleBack} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center gap-3">
            <MessageSquare className="h-16 w-16 text-muted-foreground/40" />
            <p className="text-lg font-medium text-muted-foreground">Select a message</p>
            <p className="text-sm text-muted-foreground max-w-xs">
              Brands can message you here with questions before they book.
            </p>
          </div>
        )}
      </main>
    </div>
  )
}

export default Messages
